import { useQueryClient } from "@tanstack/react-query";
import { type ReactNode, useEffect } from "react";

import { useAuthStore } from "@/store/auth";

const BASE_URL = import.meta.env.VITE_API_BASE_URL ?? "";

function wsUrl(token: string): string {
  const base = BASE_URL || window.location.origin;
  const url = new URL("/ws", base);
  url.protocol = url.protocol === "https:" ? "wss:" : "ws:";
  url.searchParams.set("token", token);
  return url.toString();
}

/** Subscribes to the backend realtime channel and refreshes live data (tasks/dashboard). */
export function RealtimeProvider({ children }: { children: ReactNode }) {
  const token = useAuthStore((s) => s.token);
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const qc = useQueryClient();

  useEffect(() => {
    if (!isAuthenticated || !token) return;
    let socket: WebSocket | null = null;
    let closed = false;
    let retry: ReturnType<typeof setTimeout>;

    const connect = () => {
      if (closed) return;
      try {
        socket = new WebSocket(wsUrl(token));
      } catch {
        return;
      }
      socket.onmessage = () => {
        qc.invalidateQueries({ queryKey: ["tasks"] });
        qc.invalidateQueries({ queryKey: ["dashboard"] });
      };
      socket.onclose = () => {
        if (!closed) retry = setTimeout(connect, 5000);
      };
    };
    connect();

    return () => {
      closed = true;
      clearTimeout(retry);
      socket?.close();
    };
  }, [token, isAuthenticated, qc]);

  return <>{children}</>;
}
